/**
 * Vorschläge und Zabbix-Übernahme für das System-Formular.
 *
 * Die Werte kommen aus GET /systems/facets (Rollen, OS, VPN, Domains, Nodes)
 * und GET /integrations/zabbix/hosts. Hier wird nur umgeformt, nicht geladen:
 * FQDN aus Kurzname+Domain, Datalist-Dedup, und das Befüllen leerer Felder
 * aus einem gewählten Zabbix-Host — gefüllte Felder bleiben unangetastet,
 * weil der Anwender sie getippt hat.
 */

import type { SystemFacetValue, SystemSuggestionValue } from '@/types/api';

export function osVersionVorschlaege(
  versions: { os_family: string; versions: SystemFacetValue[] }[] | undefined,
  osFamily: string
): SystemFacetValue[] {
  const liste = versions ?? [];
  const key = osFamily.trim().toLowerCase();
  const treffer = liste.find((e) => e.os_family === key);
  if (treffer) return treffer.versions;
  const counts = new Map<string, number>();
  for (const fam of liste) {
    for (const v of fam.versions) {
      counts.set(v.value, (counts.get(v.value) ?? 0) + v.count);
    }
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value));
}

/** FQDN-Kandidaten aus Kurzname und bekannten Domains (Kundenwerte vorn). */
export function fqdnVorschlaege(
  hostname: string,
  domains: readonly string[],
  max = 8
): string[] {
  const host = hostname.trim().toLowerCase();
  if (host === '') return [];
  // Punkt im Namen: das ist schon ein FQDN, Domains nicht dranhängen.
  if (host.includes('.')) return [host];
  const gesehen = new Set<string>();
  const liste: string[] = [];
  for (const roh of domains) {
    let domain = roh.trim().toLowerCase();
    while (domain.startsWith('.')) domain = domain.slice(1);
    if (domain === '' || gesehen.has(domain)) continue;
    gesehen.add(domain);
    liste.push(`${host}.${domain}`);
    if (liste.length >= max) break;
  }
  return liste;
}

/**
 * Serverreihenfolge behalten (Kundenwerte stehen vorn), Duplikate und leere
 * Werte streichen, auf max kappen.
 */
export function vorschlagsWerte(
  werte: readonly SystemSuggestionValue[],
  max = 30
): SystemSuggestionValue[] {
  const gesehen = new Set<string>();
  const liste: SystemSuggestionValue[] = [];
  for (const w of werte) {
    const key = w.value.trim();
    if (key === '' || gesehen.has(key)) continue;
    gesehen.add(key);
    liste.push(w);
    if (liste.length >= max) break;
  }
  return liste;
}

export interface UebernahmeWerte {
  hostname: string;
  fqdn: string;
  ipAddresses: string;
  osFamily: string;
  osVersion: string;
}

export interface UebernahmeHost {
  host: string;
  name: string;
  ips: string[];
  dns_names: string[];
  fqdn_suggestion?: string | null;
  os_family?: string | null;
  os_version?: string | null;
}

/** Kurzname: host.host bis zum ersten Punkt, sonst der sichtbare Name. */
function zabbixKurzname(host: UebernahmeHost): string {
  const quelle = host.host.trim() || host.name.trim();
  const punkt = quelle.indexOf('.');
  return punkt === -1 ? quelle : quelle.slice(0, punkt);
}

function zabbixFqdn(host: UebernahmeHost): string {
  const vorschlag = host.fqdn_suggestion?.trim() ?? '';
  if (vorschlag !== '') return vorschlag;
  const kandidaten = [host.host, ...host.dns_names];
  for (const k of kandidaten) {
    const t = k.trim();
    if (t.includes('.')) return t;
  }
  return '';
}

function zabbixIps(host: UebernahmeHost): string {
  const gesehen = new Set<string>();
  const ips: string[] = [];
  for (const ip of host.ips) {
    const t = ip.trim();
    if (t === '' || gesehen.has(t)) continue;
    gesehen.add(t);
    ips.push(t);
  }
  return ips.join(', ');
}

/**
 * Schreibt Zabbix-Werte NUR in nach trim() leere Felder. `werte` ist immer
 * ein neues Objekt; `uebernommen` listet die Labels der geschriebenen Felder.
 */
export function zabbixUebernahme(
  form: UebernahmeWerte,
  host: UebernahmeHost
): { werte: UebernahmeWerte; uebernommen: string[] } {
  const werte: UebernahmeWerte = { ...form };
  const uebernommen: string[] = [];

  if (form.hostname.trim() === '') {
    const kurz = zabbixKurzname(host);
    if (kurz !== '') {
      werte.hostname = kurz;
      uebernommen.push('Hostname');
    }
  }

  if (form.fqdn.trim() === '') {
    const fqdn = zabbixFqdn(host);
    if (fqdn !== '') {
      werte.fqdn = fqdn;
      uebernommen.push('FQDN');
    }
  }

  if (form.ipAddresses.trim() === '') {
    const ips = zabbixIps(host);
    if (ips !== '') {
      werte.ipAddresses = ips;
      uebernommen.push('IP-Adressen');
    }
  }

  if (form.osFamily.trim() === '') {
    const fam = host.os_family?.trim() ?? '';
    if (fam !== '') {
      werte.osFamily = fam.toLowerCase();
      uebernommen.push('Betriebssystem (Familie)');
    }
  }

  if (form.osVersion.trim() === '') {
    const ver = host.os_version?.trim() ?? '';
    if (ver !== '') {
      werte.osVersion = ver;
      uebernommen.push('Betriebssystem (Version)');
    }
  }

  return { werte, uebernommen };
}

/** Beschriftung in der Zabbix-Datalist: technischer Name plus Name/IP. */
export function zabbixHostLabel(host: UebernahmeHost): string {
  const teile: string[] = [];
  const name = host.name.trim();
  if (name !== '' && name !== host.host) teile.push(name);
  const ip = host.ips.find((x) => x.trim() !== '');
  if (ip) teile.push(ip.trim());
  if (teile.length === 0) return host.host;
  return `${host.host} (${teile.join(', ')})`;
}
